import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
	updateProfile,
	setIsVerifyingEmailProfile,
	verifyEmail,
	sendVerificationCodeNewEmail,
	deleteAccount,
	resetTutorials,
} from '../../features/authSlice.js'
import { CooldownButton } from '../buttons/CooldownButton.jsx'

import styles from './settingsTabs.module.scss'
import InputCustom from '../atoms/input/InputCustom.jsx'
import ButtonCustom from '../atoms/button/ButtonCustom.jsx'
import CommonModal from '../atoms/modal/CommonModal.jsx'
import CommonDeleteModal from '../atoms/modal/CommonDeleteModal.jsx'
import { toastHelper } from '../../util/toastHelper.js'
import {
	validateCanadianPhone,
	validatePassword,
} from '../../util/passwordValidation.js'
import { Typography } from '@mui/material'

// Component for managing account settings such as phone, password, and email
export const AccountSettings = () => {
	const dispatch = useDispatch()

	// Auth state selectors
	const user = useSelector((state) => state.auth.user)
	const isVerifyingEmailProfile = useSelector(
		(state) => state.auth.isVerifyingEmailProfile
	)
	const formDataInit = { currentPassword: '', newPassword: '', phone: '' }
	// Local state
	const [formData, setFormData] = useState(formDataInit)
	const [deleteAccountData, setDeleteAccountData] = useState({
		email: '',
		password: '',
	})
	const [newEmail, setNewEmail] = useState('') // New email to be verified
	const [verificationCode, setNewVerificationCode] = useState('') // Verification code from input

	// modal's
	const [showEmailModal, setShowEmailModal] = useState(false)
	const [showPasswordModal, setShowPasswordModal] = useState(false)
	const [showPhoneModal, setShowPhoneModal] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [showResetModal, setShowResetModal] = useState(false)
	const [phoneError, setPhoneError] = useState('')
	const [passwordError, setPasswordError] = useState('')
	const [currentPasswordError, setCurrentPasswordError] = useState('')
	const isResettingTutorials = useSelector(
		(state) =>
			state.auth.status === 'loading' &&
			state.auth.actionType === 'resetTutorials'
	)
	// modal's

	// Prefill phone field with existing user data when available
	useEffect(() => {
		if (user) {
			setFormData({
				phone: user.phone,
			})
		}
	}, [user])

	const handleDeleteAccount = (e) => {
		e.preventDefault()
		if (user.email !== deleteAccountData.email)
			return toastHelper('Invalid email', 'error')

		dispatch(deleteAccount(deleteAccountData.password))
	}

	// Handle phone/password form submission
	const handleSubmitPhonePassword = (e) => {
		e.preventDefault()
		const isValid = validateCanadianPhone(formData.phone)
		if (!isValid) return setPhoneError('Invalid phone number')
		dispatch(updateProfile(formData))
	}

	const handleSubmitPassword = (e) => {
		e.preventDefault()
		const error = validatePassword(formData.newPassword)
		if (error) return setPasswordError(error)
		if (formData.currentPassword === formData.newPassword)
			return setCurrentPasswordError(
				'New password should be different from current password'
			)
		dispatch(updateProfile(formData))
	}

	const handleResetTutorials = async () => {
		try {
			const resultAction = await dispatch(resetTutorials())
			if (resetTutorials.fulfilled.match(resultAction)) {
				// Clear local storage tutorials state
				const completedTutorials = JSON.parse(
					localStorage.getItem('viewedTutorials') || '[]'
				)
				localStorage.setItem(
					'viewedTutorials',
					JSON.stringify(
						completedTutorials.filter(
							(id) =>
								![
									'tasks',
									'vendor-tracker',
									'invite-partner',
								].includes(id)
						)
					)
				)
				// Show success message
				toastHelper(
					'Tutorials have been reset successfully. You will see tutorial popups again when relevant.',
					'success'
				)
			}
		} catch (error) {
			console.error('Error resetting tutorials:', error)
			toastHelper(error.message || 'Failed to reset tutorials', 'error')
		}
	}
	const handleCancelEditEmail = () => {
		setShowEmailModal(false)
		setNewEmail('')
		setNewVerificationCode('')
		dispatch(setIsVerifyingEmailProfile(false))
	}

	const handleCancelEditPhone = () => {
		setShowPhoneModal(false)
		setFormData({ ...formDataInit, phone: user.phone })
		setPhoneError('')
	}

	// Handle email verification form submission
	const handleVerifyCode = (e) => {
		e.preventDefault()
		dispatch(
			verifyEmail({
				email: user.email,
				verificationCode: verificationCode,
				newEmail: newEmail.toLowerCase(),
			})
		)
	}

	const handleClose = () => {
		setShowPasswordModal(false)
		setFormData({ ...formDataInit, phone: user.phone })
		setPasswordError('')
		setCurrentPasswordError('')
	}

	return (
		<div className={styles.profileCard}>
			<h1 className={styles.boxTitle}>Account Settings</h1>
			<div>
				<div className={styles.formBox}>
					{/* Email Button */}
					<div className={styles.formBox}>
						<div className={styles.formGroupInnerBox}>
							<InputCustom
								data-cy="email"
								name="Email"
								type="email"
								placeholder="Email"
								value={user.email}
								disabled={true}
								label="Email"
							/>
							<ButtonCustom
								data-cy="emailchange"
								type="button"
								variant="outlined"
								size="lg"
								onClick={() => setShowEmailModal(true)}
								style={{ flex: '0 0 auto' }}
							>
								Change
							</ButtonCustom>
						</div>
					</div>
					{/* Email Button */}

					<form
						onSubmit={handleSubmitPhonePassword}
						className={styles.formBox}
					>
						{/* Phone Button */}
						<div className={styles.formGroupInnerBox}>
							<InputCustom
								name="phone"
								type="tel"
								placeholder="Phone number"
								value={formData.phone}
								setDataFunction={setFormData}
								data={formData}
								disabled
								label="Phone number"
							/>
							<ButtonCustom
								data-cy="phonenumberchange"
								type="button"
								variant="outlined"
								size="lg"
								onClick={() => setShowPhoneModal(true)}
								style={{ flex: '0 0 auto' }}
							>
								Change
							</ButtonCustom>
						</div>
						{/* Phone Button */}

						<div className={styles.formBtns}>
							{/* Password Button */}
							<ButtonCustom
								data-cy="passwordchange"
								type="button"
								variant="outlined"
								size="lg"
								onClick={() => setShowPasswordModal(true)}
								style={{ flex: '0 0 auto' }}
							>
								Change Password
							</ButtonCustom>
							{/* Password Button */}

							<ButtonCustom
								variant="outlined"
								type="button"
								size="lg"
								onClick={() => setShowDeleteModal(true)}
							>
								Delete Account
							</ButtonCustom>
							<ButtonCustom
								variant="outlined"
								type="button"
								size="lg"
								// onClick={() => {
								// 	if (
								// 		window.confirm(
								// 			'Are you sure you want to reset all tutorials? This will make all tutorial popups appear again.'
								// 		)
								// 	) {
								// 		handleResetTutorials()
								// 	}
								// }}
								onClick={() => setShowResetModal(true)}
								disabled={isResettingTutorials}
							>
								{isResettingTutorials
									? 'Resetting...'
									: 'Reset Tutorials'}
							</ButtonCustom>
						</div>
					</form>
				</div>
			</div>
			{/* Modal for Email */}
			<CommonModal
				open={showEmailModal}
				handleClose={handleCancelEditEmail}
				heading={'Change Email'}
				modalBody={styles.modalBody}
			>
				<form
					onSubmit={(e) => e.preventDefault()}
					className={styles.formBox}
				>
					<div className={styles.formGroupInnerBox}>
						<InputCustom
							name="New Email"
							type="email"
							placeholder="New Email"
							value={newEmail}
							disabled={isVerifyingEmailProfile}
							onChange={(e) => setNewEmail(e.target.value)}
							label="New Email"
						/>
						{isVerifyingEmailProfile && (
							<>
								<InputCustom
									required
									maxLength={6}
									name="Verification Code"
									type="text"
									placeholder="Verification Code"
									value={verificationCode}
									onChange={(e) =>
										setNewVerificationCode(e.target.value)
									}
									label="Verification Code"
								/>
							</>
						)}
						<div className={styles.modalBtns}>
							<div>
								{isVerifyingEmailProfile && (
									<CooldownButton
										buttonText="Resend verification code"
										size="lg"
										cooldownSeconds={15}
										dispatchFunction={
											sendVerificationCodeNewEmail
										}
										dispatchData={newEmail.toLowerCase()}
									/>
								)}
							</div>
							<div className={styles.modalBtnsLeft}>
								<ButtonCustom
									variant="outlined"
									size="lg"
									onClick={handleCancelEditEmail}
								>
									Cancel
								</ButtonCustom>
								{isVerifyingEmailProfile ? (
									<ButtonCustom
										size="lg"
										onClick={handleVerifyCode}
									>
										Verify
									</ButtonCustom>
								) : (
									<ButtonCustom
										size="lg"
										onClick={() =>
											dispatch(
												sendVerificationCodeNewEmail(
													newEmail.toLowerCase()
												)
											)
										}
									>
										Send verification code
									</ButtonCustom>
								)}
							</div>
						</div>
					</div>
				</form>
			</CommonModal>
			{/* Modal for Email */}

			{/* Modal for Phone */}
			<CommonModal
				open={showPhoneModal}
				handleClose={handleCancelEditPhone}
				heading={'Change Phone'}
				modalBody={styles.modalBody}
			>
				<form
					onSubmit={handleSubmitPhonePassword}
					className={styles.formBox}
				>
					<div className={styles.formGroupInnerBox}>
						<InputCustom
							name="phone"
							type="tel"
							placeholder="Phone number"
							value={formData.phone}
							setDataFunction={setFormData}
							data={formData}
							data_cy="phoneNumber"
							label="Phone number"
							error={!!phoneError}
							changeFunction={() => setPhoneError('')}
						/>
						{phoneError && (
							<p className="text-error">{phoneError}</p>
						)}
						<div className={styles.modalBtns}>
							<ButtonCustom
								variant="outlined"
								size="lg"
								onClick={handleCancelEditPhone}
							>
								Cancel
							</ButtonCustom>
							<ButtonCustom type="submit" size="lg">
								Save
							</ButtonCustom>
						</div>
					</div>
				</form>
			</CommonModal>
			{/* Modal for Phone */}

			{/* Modal for Password */}
			<CommonModal
				open={showPasswordModal}
				handleClose={handleClose}
				heading={'Change Password'}
				modalBody={styles.modalBody}
			>
				<form
					onSubmit={handleSubmitPassword}
					className={styles.formBox}
				>
					<div className={styles.formGroupInnerBox}>
						<Typography>New Password</Typography>
						<InputCustom
							name="newPassword"
							type="password"
							placeholder="New Password"
							enableShowPassword={true}
							value={formData.newPassword}
							setDataFunction={setFormData}
							data={formData}
							data_cy="newPassword"
							required={true}
							eyeIcon={true}
							error={!!passwordError}
							changeFunction={() => {
								setPasswordError('')
								setCurrentPasswordError('')
							}}
						/>
						{passwordError && (
							<p className="text-error">{passwordError}</p>
						)}
					</div>
					<div className={styles.formGroup}>
						<Typography>Current Password</Typography>
						<InputCustom
							name="currentPassword"
							type="password"
							placeholder="Current Password"
							setDataFunction={setFormData}
							data={formData}
							data_cy="currentPassword"
							required={true}
							enableShowPassword={true}
							eyeIcon={true}
							error={!!currentPasswordError}
							changeFunction={() => setCurrentPasswordError('')}
						/>
						{currentPasswordError && (
							<p className="text-error">{currentPasswordError}</p>
						)}
					</div>
					<div className={styles.modalBtns}>
						<ButtonCustom
							variant="outlined"
							size="lg"
							onClick={handleClose}
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							data-cy="passwordsave"
							type="submit"
							size="lg"
						>
							Change Password
						</ButtonCustom>
					</div>
				</form>
			</CommonModal>
			{/* Modal for Password */}

			{/* Modal for Delete */}
			<CommonDeleteModal
				open={showDeleteModal}
				handleClose={() => setShowDeleteModal(false)}
				heading={'Delete Account'}
				subHeading={
					'Are you sure you want to delete this Account? Please enter your email and password to proceed.'
				}
				modalBody={styles.modalBody}
			>
				<form onSubmit={handleDeleteAccount} className={styles.formBox}>
					<InputCustom
						placeholder="Account Email"
						name="email"
						type="email"
						value={deleteAccountData.email}
						autoFocus={true}
						setDataFunction={setDeleteAccountData}
						data={deleteAccountData}
						required={true}
					/>
					<InputCustom
						placeholder="Password"
						name="password"
						type="password"
						value={deleteAccountData.password}
						setDataFunction={setDeleteAccountData}
						data={deleteAccountData}
						required={true}
						eyeIcon={true}
						enableShowPassword={true}
					/>
					<div
						className={styles.modalBtns}
						style={{ justifyContent: 'center', marginTop: '0' }}
					>
						<ButtonCustom
							variant="outlined"
							type="button"
							size="lg"
							onClick={() => setShowDeleteModal(false)}
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom type="submit" size="lg">
							Delete Account
						</ButtonCustom>
					</div>
				</form>
			</CommonDeleteModal>
			{/* Modal for Delete */}
			{/* reset tutorials modal */}
			<CommonDeleteModal
				Icon={false}
				open={showResetModal}
				handleClose={() => setShowResetModal(false)}
				heading={'Reset Tutorials'}
				subHeading={
					'Are you sure you want to reset all tutorials? This will make all tutorial popups appear again.'
				}
				modalBody={styles.modalBody}
			>
				<form
					onSubmit={handleResetTutorials}
					className={styles.formBox}
				>
					<div
						className={styles.modalBtns}
						style={{ justifyContent: 'center', marginTop: '0' }}
					>
						<ButtonCustom
							variant="default"
							type="button"
							size="lg"
							onClick={() => handleResetTutorials()}
						>
							Yes
						</ButtonCustom>
						<ButtonCustom
							variant="outlined"
							type="button"
							size="lg"
							onClick={() => setShowResetModal(false)}
						>
							No
						</ButtonCustom>
					</div>
				</form>
			</CommonDeleteModal>
		</div>
	)
}
